import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  styled,
  CircularProgress,
} from "@mui/material";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";
import { getUserAccountStatistics } from "../../features/dashboard-module/dashboardAction";
import { resetDashboardState } from "../../features/dashboard-module/dashboardSlice";

ChartJS.register(ArcElement, Tooltip, Legend);

const Container = styled(Box)(({ theme }) => ({
  minHeight: "auto",
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    transform: "scale(0.95)",
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Header = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 700,
  color: "#4b5b5d",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.2rem",
  },
}));

const StatCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullWidth",
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  background: "linear-gradient(to bottom, #f5f0e7, #fff)",
  borderRadius: 8,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  padding: theme.spacing(1.5),
  width: fullWidth ? "100%" : "48%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
}));

const StatsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const StatTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: "#4b5b5d",
  fontWeight: 600,
  [theme.breakpoints.down("md")]: {
    fontSize: "0.85rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#4b5b5d",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.8rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  width: "65px",
  height: "65px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
  borderRadius: "50%",
  [theme.breakpoints.down("sm")]: {
    width: "70px",
    height: "70px",
  },
}));

const PlanCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #f5f0e7, #fff)",
  borderRadius: 8,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

const ProgressBarContainer = styled(Box)(({ theme }) => ({
  width: "60%",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  height: "15px",
  backgroundColor: "#e0e0e0",
  borderRadius: 10,
  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
  overflow: "hidden",
}));

const ProgressFill = styled(Box)<{ progress: number }>(({ progress }) => ({
  height: "100%",
  background: "linear-gradient(to right, #4b5b5d, #758d8e)",
  width: `${progress}%`,
  borderRadius: 5,
}));

const UpgradeButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(1, 2.5),
  "&:hover": {
    backgroundColor: "#354142",
  },
  [theme.breakpoints.down("md")]: {
    width: "30%",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const StatContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flex: 1,
});

const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  minHeight: "200px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const AccountStatistics: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { statistics, loading, error } = useSelector(
    (state: RootState) => state.dashboard
  );

  useEffect(() => {
    dispatch(getUserAccountStatistics());

    return () => {
      dispatch(resetDashboardState());
    };
  }, [dispatch]);

  // Extract data from the new statistics structure
  const siteEstimator = statistics?.siteEstimator;
  const fleetEstimator = statistics?.fleetEstimator;
  const planExpiresIn = statistics?.planExpiresIn || null;
  const planDuration = statistics?.planDuration || "";

  // Check if only site estimator is available (no fleet estimator)
  const hasOnlySiteEstimator =
    siteEstimator &&
    siteEstimator.quotaLimit > 0 &&
    (!fleetEstimator || fleetEstimator.quotaLimit === 0);
  const hasOnlyFleetEstimator =
    fleetEstimator &&
    fleetEstimator.quotaLimit > 0 &&
    (!siteEstimator || siteEstimator.quotaLimit === 0);
  const hasBothEstimators =
    siteEstimator &&
    siteEstimator.quotaLimit > 0 &&
    fleetEstimator &&
    fleetEstimator.quotaLimit > 0;

  // Generate pie chart data for used reports
  const generateUsedChartData = (estimator: any, label: string) => {
    if (!estimator) return { labels: [], datasets: [] };

    return {
      labels: ["Used", "Remaining"],
      datasets: [
        {
          data: [estimator.totalReportsUsed, estimator.totalReportsRemaining],
          backgroundColor: ["#4b5b5d", "#f5f0e7"],
          borderWidth: 1,
        },
      ],
    };
  };

  // Options for charts (show only "Used" in tooltip)
  const usedChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        bodyFont: {
          size: 10,
        },
        filter: (tooltipItem: any) => {
          return tooltipItem.label === "Used";
        },
      },
    },
  };

  // Calculate decrease indicator for plan expiration based on planDuration
  const getTotalDurationDays = (duration: string): number => {
    return duration === "monthly" ? 30 : duration === "yearly" ? 365 : 30;
  };

  const totalDurationDays = getTotalDurationDays(planDuration);
  const progress =
    planExpiresIn !== null ? (planExpiresIn / totalDurationDays) * 100 : 0;

  // Format planExpiresIn for display
  const formatPlanExpiresIn = (days: number | null): string => {
    if (days === null || planDuration === "") return "Not available";
    if (days <= 0) return "Expired";
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    return `${months} Month${months !== 1 ? "s" : ""} and ${remainingDays} Day${
      remainingDays !== 1 ? "s" : ""
    }`;
  };

  if (loading) {
    return (
      <Container>
        <Header>Account Statistics</Header>
        <LoaderContainer>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: "#4b5b5d",
            }}
          />
          <LoaderText>Loading account statistics...</LoaderText>
        </LoaderContainer>
      </Container>
    );
  }

  if (error && !error.includes("No reports found")) {
    return <Container>Error: {error}</Container>;
  }

  // Show no-data state if no statistics available
  if (!statistics || (!siteEstimator && !fleetEstimator)) {
    return (
      <Container>
        <Header>Account Statistics</Header>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#4b5b5d",
              marginBottom: 2,
            }}
          >
            No account statistics available yet
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "#666",
              marginBottom: 3,
            }}
          >
            Generate your first report to see account statistics
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/main/generate-reports")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(to top, #758d8e, #4b5b5d)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              color: "#fff",
              borderRadius: 8,
              padding: "12px 24px",
              "&:hover": {
                backgroundColor: "#354142",
              },
            }}
          >
            Generate Report
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Header>Account Statistics</Header>

      {/* Used Statistics Row */}
      <StatsRow>
        {/* Site Estimator Used Card */}
        {siteEstimator && siteEstimator.quotaLimit > 0 && (
          <StatCard fullWidth={hasOnlySiteEstimator}>
            <StatContent>
              <StatTitle>Site Reports Used</StatTitle>
              <StatValue>{`${siteEstimator.totalReportsUsed} / ${siteEstimator.quotaLimit} Reports`}</StatValue>
            </StatContent>
            <ChartContainer>
              <Pie
                data={generateUsedChartData(siteEstimator, "Site")}
                options={usedChartOptions}
              />
            </ChartContainer>
          </StatCard>
        )}

        {/* Fleet Estimator Used Card */}
        {fleetEstimator && fleetEstimator.quotaLimit > 0 && (
          <StatCard fullWidth={hasOnlyFleetEstimator}>
            <StatContent>
              <StatTitle>Fleet Reports Used</StatTitle>
              <StatValue>{`${fleetEstimator.totalReportsUsed} / ${fleetEstimator.quotaLimit} Reports`}</StatValue>
            </StatContent>
            <ChartContainer>
              <Pie
                data={generateUsedChartData(fleetEstimator, "Fleet")}
                options={usedChartOptions}
              />
            </ChartContainer>
          </StatCard>
        )}
      </StatsRow>

      {/* No Estimators Available */}
      {(!siteEstimator || siteEstimator.quotaLimit === 0) &&
        (!fleetEstimator || fleetEstimator.quotaLimit === 0) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 4,
              textAlign: "center",
              backgroundColor: "#f9f9f9",
              borderRadius: 2,
              marginBottom: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "1rem",
                color: "#4b5b5d",
                marginBottom: 1,
              }}
            >
              No estimator quotas available
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "#666",
              }}
            >
              Your current plan doesn't include site or fleet estimator reports
            </Typography>
          </Box>
        )}

      {/* Plan Information */}
      <PlanCard>
        <ProgressBarContainer>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "#4b5b5d",
              mb: 1,
            }}
          >
            Plan Expires in
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "#4b5b5d",
              mb: 1,
            }}
          >
            {formatPlanExpiresIn(planExpiresIn)}
          </Typography>
          <ProgressBar>
            <ProgressFill progress={Math.min(progress, 100)} />
          </ProgressBar>
        </ProgressBarContainer>
        <UpgradeButton
          variant="contained"
          onClick={() => {
            navigate("/main/plans");
          }}
        >
          Upgrade Plan
        </UpgradeButton>
      </PlanCard>
    </Container>
  );
};

export default AccountStatistics;
